import { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import type { Scanner } from '@packages/shared-types';

import { LoadingLogo } from '@/components/shared/LoadingLogo';

interface ScannerStatusChartProps {
  scanners: Scanner[];
  isLoading?: boolean;
}

const COLORS = {
  active: '#22c55e', // green-500
  inactive: '#6b7280', // gray-500
  maintenance: '#eab308', // yellow-500
  charging: '#3b82f6', // blue-500
};

export function ScannerStatusChart({
  scanners,
  isLoading,
}: ScannerStatusChartProps): JSX.Element {
  const chartData = useMemo(() => {
    const statusCounts = scanners.reduce(
      (acc, scanner) => {
        acc[scanner.status] = (acc[scanner.status] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    return [
      {
        name: 'Active',
        value: statusCounts.active || 0,
        color: COLORS.active,
      },
      {
        name: 'Inactive',
        value: statusCounts.inactive || 0,
        color: COLORS.inactive,
      },
      {
        name: 'Maintenance',
        value: statusCounts.maintenance || 0,
        color: COLORS.maintenance,
      },
      {
        name: 'Charging',
        value: statusCounts.charging || 0,
        color: COLORS.charging,
      },
    ].filter((item) => item.value > 0);
  }, [scanners]);

  if (isLoading) {
    return (
      <div className="flex h-[300px] flex-col items-center justify-center gap-3">
        <LoadingLogo size="sm" />
        <span className="text-sm text-muted-foreground">Loading chart...</span>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="flex h-[300px] items-center justify-center">
        <div className="text-sm text-muted-foreground">No scanner data</div>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
          outerRadius={80}
          fill="#8884d8"
          dataKey="value"
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            backgroundColor: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '0.5rem',
            color: 'hsl(var(--card-foreground))',
          }}
        />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}
